import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { getAuditLog } from '../utils/api';
import AgentLayout from '../components/AgentLayout';

const actionLabels = {
  approve_agent: { label: 'Approved Agent', color: 'bg-green-100 text-green-800' },
  reject_agent: { label: 'Rejected Agent', color: 'bg-red-100 text-red-800' },
  activate_agent: { label: 'Activated Agent', color: 'bg-green-100 text-green-800' },
  deactivate_agent: { label: 'Deactivated Agent', color: 'bg-yellow-100 text-yellow-800' },
  change_role: { label: 'Changed Role', color: 'bg-purple-100 text-purple-800' },
  admin_reset_password: { label: 'Reset Password', color: 'bg-blue-100 text-blue-800' },
  delete_agent: { label: 'Deleted Agent', color: 'bg-red-100 text-red-800' },
  add_note: { label: 'Added Note', color: 'bg-gray-100 text-gray-800' },
  create_team: { label: 'Created Team', color: 'bg-teal-100 text-teal-800' },
  delete_team: { label: 'Deleted Team', color: 'bg-red-100 text-red-800' },
  update_teams: { label: 'Updated Teams', color: 'bg-blue-100 text-blue-800' },
  update_report_settings: { label: 'Updated Report Settings', color: 'bg-indigo-100 text-indigo-800' },
};

const AdminAuditLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    const parsed = JSON.parse(info);
    if (parsed.role !== 'admin' && parsed.role !== 'super_admin') {
      navigate('/agent/dashboard'); return;
    }
    loadLogs(token);
  }, [navigate]);

  const loadLogs = async (token) => {
    try {
      setIsLoading(true);
      const result = await getAuditLog(token, 200);
      if (result.success) setLogs(result.logs || []);
    } catch (e) {
      toast.error('Failed to load audit log');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLogs = logs.filter(l => {
    if (actionFilter !== 'all' && l.action !== actionFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!l.performedByName?.toLowerCase().includes(s) && !l.details?.toLowerCase().includes(s) && !l.action?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const uniqueActions = [...new Set(logs.map(l => l.action))];

  if (isLoading) {
    return (
      <AgentLayout title="Audit Log">
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Audit Log">
      <div data-testid="admin-audit-log-page">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by admin name or details..." className="w-full pl-10 pr-4 py-2 border rounded-md text-sm" data-testid="audit-search" />
          </div>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm" data-testid="audit-action-filter">
            <option value="all">All Actions</option>
            {uniqueActions.map(a => (
              <option key={a} value={a}>{actionLabels[a]?.label || a}</option>
            ))}
          </select>
          <button onClick={() => loadLogs(localStorage.getItem('agentToken'))} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-brand-blue">
            <RefreshCw size={14} /> Refresh
          </button>
          <span className="text-xs text-gray-500">{filteredLogs.length} entries</span>
        </div>

        {/* Log Entries */}
        {filteredLogs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <ScrollText size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No audit log entries found</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]" data-testid="audit-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Timestamp</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Performed By</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLogs.map(log => {
                  const actionInfo = actionLabels[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-600' };
                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${actionInfo.color}`}>{actionInfo.label}</span></td>
                      <td className="px-4 py-3 font-medium text-gray-900">{log.performedByName}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{log.details}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AdminAuditLog;
